import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Ship, ClipboardCheck, AlertTriangle, FileText, TrendingUp, TrendingDown,
  CheckCircle2, Clock, Anchor, BarChart3, Eye, ArrowUpRight,
  MapPin, Anchor as AnchorIcon, Activity, Zap, Shield, Calendar
} from 'lucide-react';
import { useAppStore } from '../store/useAppStore';
import { formatDate, getScoreColor, getScoreBgColor } from '../utils/helpers';
import { mockFleets, mockObservations, mockCorrectiveActions } from '../data/mockData';

function KPICard({ title, value, change, trend, icon: Icon, color, subtitle }: {
  title: string; value: string | number; change?: number; trend?: 'up' | 'down' | 'stable';
  icon: React.ElementType; color: string; subtitle?: string;
}) {
  return (
    <div className="bg-white rounded-2xl p-5 border border-slate-100 hover:shadow-lg hover:shadow-slate-200/50 transition-all duration-300 group">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-slate-500 mb-1">{title}</p>
          <p className="text-3xl font-bold text-slate-900 tracking-tight">{value}</p>
          {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
          {change !== undefined && (
            <div className={`flex items-center gap-1 mt-2 text-xs font-semibold ${trend === 'up' ? 'text-emerald-600' : trend === 'down' ? 'text-red-500' : 'text-slate-500'}`}>
              {trend === 'up' ? <TrendingUp className="w-3.5 h-3.5" /> : trend === 'down' ? <TrendingDown className="w-3.5 h-3.5" /> : null}
              <span>{change > 0 ? '+' : ''}{change}%</span>
              <span className="text-slate-400 font-normal">vs last month</span>
            </div>
          )}
        </div>
        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 ${color} group-hover:scale-110 transition-transform duration-300`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </div>
  );
}

function MiniBarChart({ data, color }: { data: number[]; color: string }) {
  const max = Math.max(...data);
  return (
    <div className="flex items-end gap-1 h-16">
      {data.map((v, i) => (
        <div key={i} className="flex-1 rounded-t-sm transition-all duration-500" style={{ height: `${(v / max) * 100}%`, backgroundColor: color, opacity: 0.3 + (i / data.length) * 0.7 }} />
      ))}
    </div>
  );
}

function DonutChart({ segments, size = 120 }: { segments: { value: number; color: string; label: string }[]; size?: number }) {
  const total = segments.reduce((s, seg) => s + seg.value, 0);
  let cumulative = 0;
  const r = size / 2 - 10;
  const circumference = 2 * Math.PI * r;

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {segments.map((seg, i) => {
          const pct = seg.value / total;
          const offset = circumference * (1 - cumulative / total);
          const dashLen = circumference * pct;
          cumulative += seg.value;
          return (
            <circle key={i} cx={size / 2} cy={size / 2} r={r} fill="none"
              stroke={seg.color} strokeWidth="8"
              strokeDasharray={`${dashLen} ${circumference - dashLen}`}
              strokeDashoffset={offset}
              strokeLinecap="round"
              style={{ transform: 'rotate(-90deg)', transformOrigin: '50% 50%', transition: 'all 1s ease-out' }}
            />
          );
        })}
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="text-center">
          <p className="text-2xl font-bold text-slate-800">{total}</p>
          <p className="text-[10px] text-slate-400">Total</p>
        </div>
      </div>
    </div>
  );
}

function HorizontalBarChart({ items }: { items: { label: string; value: number; max: number; color: string }[] }) {
  return (
    <div className="space-y-3">
      {items.map((item, i) => (
        <div key={i}>
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm text-slate-600 truncate">{item.label}</span>
            <span className="text-sm font-semibold text-slate-800 ml-2">{item.value}</span>
          </div>
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-all duration-1000 ease-out" style={{ width: `${(item.value / item.max) * 100}%`, backgroundColor: item.color }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function LineChart({ data, color, height = 80 }: { data: { x: string; y: number }[]; color: string; height?: number }) {
  const max = Math.max(...data.map(d => d.y));
  const min = Math.min(...data.map(d => d.y));
  const range = max - min || 1;
  const w = 300;
  const h = height;
  const points = data.map((d, i) => `${(i / (data.length - 1)) * w},${h - ((d.y - min) / range) * h}`).join(' ');

  return (
    <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="overflow-visible">
      <defs>
        <linearGradient id={`grad-${color.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polyline points={points} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      {data.map((d, i) => (
        <circle key={i} cx={(i / (data.length - 1)) * w} cy={h - ((d.y - min) / range) * h} r="3" fill={color} className="opacity-0 hover:opacity-100 transition-opacity" />
      ))}
    </svg>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { vessels, inspections, correctiveActions } = useAppStore();
  const [timeRange, setTimeRange] = useState('6m');

  const activeVessels = vessels.filter(v => v.status === 'active' || v.status === 'under_inspection').length;
  const openInspections = inspections.filter(i => i.status === 'in_progress' || i.status === 'under_review').length;
  const completedThisMonth = inspections.filter(i => i.status === 'approved' || i.status === 'completed').length;
  const criticalFindings = mockObservations.filter(o => o.severity === 'critical').length;
  const overdueActions = correctiveActions.filter(c => c.isOverdue).length;
  const avgScore = (vessels.reduce((s, v) => s + (v.lastRightShipScore || 0), 0) / vessels.filter(v => v.lastRightShipScore).length).toFixed(1);

  const complianceData = [
    { x: 'Feb', y: 82 }, { x: 'Mar', y: 85 }, { x: 'Apr', y: 83 },
    { x: 'May', y: 88 }, { x: 'Jun', y: 87 }, { x: 'Jul', y: 89 },
  ];

  const findingsByType = [
    { label: 'Fire Safety', value: 12, max: 15, color: '#EF4444' },
    { label: 'Machinery', value: 9, max: 15, color: '#F59E0B' },
    { label: 'Environmental', value: 7, max: 15, color: '#10B981' },
    { label: 'Navigation', value: 5, max: 15, color: '#3B82F6' },
    { label: 'Documentation', value: 8, max: 15, color: '#8B5CF6' },
    { label: 'Life-Saving', value: 4, max: 15, color: '#06B6D4' },
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Fleet Overview</h1>
          <p className="text-sm text-slate-500 mt-1">Welcome back, Sarah. Here's your fleet status today.</p>
        </div>
        <div className="flex items-center gap-2">
          {['1m', '3m', '6m', '1y'].map(range => (
            <button key={range} onClick={() => setTimeRange(range)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${timeRange === range ? 'bg-[#0A1628] text-white shadow-md' : 'bg-white text-slate-500 border border-slate-200 hover:border-slate-300'}`}>
              {range}
            </button>
          ))}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard title="Active Vessels" value={activeVessels} change={5} trend="up" icon={Ship} color="bg-sky-50 text-sky-600" subtitle="3 fleets" />
        <KPICard title="Fleet Readiness" value={`${avgScore}`} change={3.2} trend="up" icon={Shield} color="bg-emerald-50 text-emerald-600" subtitle="RightShip avg score" />
        <KPICard title="Open Inspections" value={openInspections} change={-8} trend="down" icon={ClipboardCheck} color="bg-amber-50 text-amber-600" subtitle="2 in progress" />
        <KPICard title="Overdue Actions" value={overdueActions} change={12} trend="up" icon={AlertTriangle} color="bg-red-50 text-red-600" subtitle="3 critical" />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Compliance Trend */}
        <div className="lg:col-span-2 bg-white rounded-2xl p-5 border border-slate-100">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-semibold text-slate-800">Compliance Trend</h3>
              <p className="text-xs text-slate-400 mt-0.5">Fleet-wide compliance percentage over time</p>
            </div>
            <div className="flex items-center gap-2 text-xs text-emerald-600 font-medium bg-emerald-50 px-2.5 py-1 rounded-full">
              <TrendingUp className="w-3.5 h-3.5" /> +7% overall
            </div>
          </div>
          <LineChart data={complianceData} color="#0EA5E9" height={120} />
          <div className="flex justify-between text-[10px] text-slate-400 mt-2 px-1">
            {complianceData.map(d => <span key={d.x}>{d.x}</span>)}
          </div>
        </div>

        {/* Findings by Severity */}
        <div className="bg-white rounded-2xl p-5 border border-slate-100">
          <h3 className="font-semibold text-slate-800 mb-1">Findings by Severity</h3>
          <p className="text-xs text-slate-400 mb-4">All open findings across fleet</p>
          <div className="flex justify-center mb-4">
            <DonutChart segments={[
              { value: criticalFindings, color: '#EF4444', label: 'Critical' },
              { value: 8, color: '#F97316', label: 'Major' },
              { value: 15, color: '#EAB308', label: 'Minor' },
              { value: 6, color: '#94A3B8', label: 'Observation' },
            ]} />
          </div>
          <div className="space-y-2">
            {[
              { label: 'Critical', count: criticalFindings, color: 'bg-red-500' },
              { label: 'Major', count: 8, color: 'bg-orange-500' },
              { label: 'Minor', count: 15, color: 'bg-yellow-500' },
              { label: 'Observation', count: 6, color: 'bg-slate-400' },
            ].map(item => (
              <div key={item.label} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <div className={`w-2.5 h-2.5 rounded-full ${item.color}`} />
                  <span className="text-slate-600">{item.label}</span>
                </div>
                <span className="font-semibold text-slate-800">{item.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Fleet Table & Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Fleet Performance */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-100 overflow-hidden">
          <div className="p-5 pb-3 flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-slate-800">Fleet Performance</h3>
              <p className="text-xs text-slate-400 mt-0.5">Vessels by RightShip readiness score</p>
            </div>
            <button onClick={() => navigate('/app/fleet')} className="text-xs text-sky-600 hover:text-sky-700 font-medium flex items-center gap-1">
              View all <ArrowUpRight className="w-3 h-3" />
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead>
                <tr className="border-t border-slate-100 bg-slate-50/50">
                  <th className="text-left px-5 py-3 font-medium text-slate-500 text-xs">Vessel</th>
                  <th className="text-left px-5 py-3 font-medium text-slate-500 text-xs hidden sm:table-cell">Status</th>
                  <th className="text-center px-5 py-3 font-medium text-slate-500 text-xs hidden md:table-cell">Score</th>
                  <th className="text-center px-5 py-3 font-medium text-slate-500 text-xs hidden lg:table-cell">Findings</th>
                  <th className="text-center px-5 py-3 font-medium text-slate-500 text-xs hidden lg:table-cell">CAPAs</th>
                  <th className="text-left px-5 py-3 font-medium text-slate-500 text-xs hidden md:table-cell">Location</th>
                </tr>
              </thead>
              <tbody>
                {vessels.slice(0, 6).map(v => (
                  <tr key={v.id} className="border-t border-slate-50 hover:bg-slate-50/50 cursor-pointer transition-colors" onClick={() => navigate('/app/fleet')}>
                    <td className="px-5 py-3">
                      <div>
                        <p className="font-medium text-slate-800">{v.name}</p>
                        <p className="text-xs text-slate-400">{v.imoNumber} · {v.type}</p>
                      </div>
                    </td>
                    <td className="px-5 py-3 hidden sm:table-cell">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                        v.status === 'active' ? 'bg-emerald-50 text-emerald-700' :
                        v.status === 'under_inspection' ? 'bg-blue-50 text-blue-700' :
                        v.status === 'dry_dock' ? 'bg-amber-50 text-amber-700' : 'bg-slate-100 text-slate-500'
                      }`}>
                        {v.status === 'dry_dock' ? 'Dry Dock' : v.status === 'under_inspection' ? 'Inspecting' : v.status === 'active' ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-center hidden md:table-cell">
                      <span className={`inline-flex items-center justify-center w-10 h-10 rounded-xl text-sm font-bold text-white ${getScoreBgColor(v.lastRightShipScore || 0)}`}>
                        {v.lastRightShipScore?.toFixed(1) || '—'}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-center hidden lg:table-cell">
                      <span className={`font-semibold ${v.openFindings > 5 ? 'text-red-600' : v.openFindings > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                        {v.openFindings}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-center hidden lg:table-cell">
                      <span className={`font-semibold ${v.overdueActions > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                        {v.overdueActions}
                      </span>
                    </td>
                    <td className="px-5 py-3 hidden md:table-cell">
                      <div className="flex items-center gap-1.5 text-slate-500">
                        <MapPin className="w-3.5 h-3.5" />
                        <span className="text-xs">{v.location.port}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Recent Activity & Findings by Category */}
        <div className="space-y-4">
          {/* Findings by Category */}
          <div className="bg-white rounded-2xl p-5 border border-slate-100">
            <h3 className="font-semibold text-slate-800 mb-4">Findings by Category</h3>
            <HorizontalBarChart items={findingsByType} />
          </div>

          {/* Upcoming Inspections */}
          <div className="bg-white rounded-2xl p-5 border border-slate-100">
            <h3 className="font-semibold text-slate-800 mb-1">Upcoming Inspections</h3>
            <p className="text-xs text-slate-400 mb-3">Next 30 days</p>
            <div className="space-y-3">
              {[
                { vessel: 'MV Phoenix Rising', date: 'Aug 5, 2026', type: 'Follow-up', color: 'bg-orange-500' },
                { vessel: 'MV Nordic Star', date: 'Aug 28, 2026', type: 'Initial', color: 'bg-blue-500' },
                { vessel: 'MV Pacific Titan', date: 'Sep 15, 2026', type: 'Periodic', color: 'bg-emerald-500' },
              ].map(item => (
                <div key={item.vessel} className="flex items-start gap-3 p-2 rounded-xl hover:bg-slate-50 transition-colors">
                  <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${item.color}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{item.vessel}</p>
                    <p className="text-xs text-slate-400">{item.date} · {item.type}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* AI Readiness */}
          <div className="bg-gradient-to-br from-[#0A1628] to-[#1E3A5F] rounded-2xl p-5 text-white">
            <div className="flex items-center gap-2 mb-3">
              <Zap className="w-5 h-5 text-amber-400" />
              <h3 className="font-semibold">AI Readiness Prediction</h3>
            </div>
            <div className="space-y-3">
              {[
                { name: 'MV Atlantic Pioneer', score: 92, color: 'bg-emerald-400' },
                { name: 'MV Pacific Titan', score: 78, color: 'bg-amber-400' },
                { name: 'MV Phoenix Rising', score: 54, color: 'bg-red-400' },
              ].map(v => (
                <div key={v.name}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-slate-300 truncate">{v.name}</span>
                    <span className="font-bold">{v.score}%</span>
                  </div>
                  <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${v.color}`} style={{ width: `${v.score}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Inspection Status Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {[
          { label: 'Draft', count: 1, color: 'bg-slate-400', icon: FileText },
          { label: 'In Progress', count: 1, color: 'bg-blue-500', icon: Activity },
          { label: 'Completed', count: 1, color: 'bg-emerald-500', icon: CheckCircle2 },
          { label: 'Under Review', count: 1, color: 'bg-amber-500', icon: Eye },
          { label: 'Approved', count: 2, color: 'bg-green-500', icon: CheckCircle2 },
        ].map(item => (
          <div key={item.label} className="bg-white rounded-2xl p-4 border border-slate-100 hover:shadow-md transition-all group cursor-pointer" onClick={() => navigate('/app/inspections')}>
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${item.color} bg-opacity-10`}>
                <item.icon className={`w-5 h-5 text-opacity-100`} style={{ color: item.color.replace('bg-', '').replace('-500', '').replace('slate-400', '#94A3B8') }} />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{item.count}</p>
                <p className="text-xs text-slate-500">{item.label}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
